import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import MetricsPanel from './components/MetricsPanel';
import TrendAnalysisChart from './components/TrendAnalysisChart';
import CategoryBreakdownChart from './components/CategoryBreakdownChart';
import LocationPerformanceChart from './components/LocationPerformanceChart';
import ObservationsTable from './components/ObservationsTable';
import PriorityAlertsPanel from './components/PriorityAlertsPanel';
import AIVisionIntegration from './components/AIVisionIntegration';

const UnsafeActsConditionsAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [dateRange, setDateRange] = useState('last-30-days');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'last-7-days', label: 'Last 7 Days' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const locationOptions = [
    { value: 'all', label: 'All Locations' },
    { value: 'plant-1', label: 'Plant 1 - Main Facility' },
    { value: 'plant-2', label: 'Plant 2 - North Wing' },
    { value: 'plant-3', label: 'Plant 3 - South Wing' },
    { value: 'warehouse-1', label: 'Warehouse 1' }
  ];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'unsafe-act', label: 'Unsafe Acts' },
    { value: 'unsafe-condition', label: 'Unsafe Conditions' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'reported', label: 'Reported' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'rectified', label: 'Rectified' },
    { value: 'overdue', label: 'Overdue' }
  ];

  const handleExportReport = () => {
    console.log('Exporting comprehensive safety report...');
  };

  const handleNewObservation = () => {
    navigate('/unsafe-acts-conditions-entry-form');
  };

  return (
    <div className="min-h-screen bg-background p-6 lg:p-8">
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Unsafe Acts & Conditions Analytics</h1>
            <p className="text-muted-foreground mt-1">
              Comprehensive monitoring and analysis of workplace safety observations
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExportReport}
              className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors"
            >
              <Icon name="Download" size={20} />
              <span className="hidden md:inline">Export Report</span>
            </button>
            <button
              onClick={handleNewObservation}
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              <Icon name="Plus" size={20} />
              <span className="hidden md:inline">New Observation</span>
            </button>
          </div>
        </div>
      </div>
      <div className="mb-6 bg-card border border-border rounded-lg p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-foreground mb-2">Date Range</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e?.target?.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {dateRangeOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-foreground mb-2">Location</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e?.target?.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {locationOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-foreground mb-2">Category</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e?.target?.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {categoryOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-foreground mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e?.target?.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              {statusOptions?.map(option => (
                <option key={option?.value} value={option?.value}>
                  {option?.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
      <MetricsPanel />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <TrendAnalysisChart />
        <CategoryBreakdownChart />
      </div>
      <LocationPerformanceChart />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <div className="lg:col-span-2">
          <ObservationsTable />
        </div>
        <div>
          <PriorityAlertsPanel />
        </div>
      </div>
      <AIVisionIntegration />
    </div>
  );
};

export default UnsafeActsConditionsAnalyticsDashboard;